import cx from 'classnames';
import useTranslation from 'next-translate/useTranslation';

import useLinkProps from '../../../hooks/use-link-props';
import BackgroundLink from '../../ui/links/background';
import RelatedProduct from '../../ui/v2/products/related';
import { Carousel, CarouselBullets, CarouselContent, CarouselItem } from '../../ui/carousel-v2';

type RelatedProductsProps = {
  relatedProducts: TProductListItem[];
  bgColor?: string;
};

const RelatedProducts = ({
  relatedProducts,
  bgColor = 'bg-neutral-10',
}: RelatedProductsProps) => {
  const { t } = useTranslation();
  const getLinks = useLinkProps();
  const referenceVisible = relatedProducts.filter(el => el.car?.engine || el.engine_code);

  const gtmEventOnClick = (product: TProductListItem, index: number) => {
    window?.dataLayer.push({
      event: 'eec.impressionClick',
      ecommerce: {
        currencyCode: 'EUR', // currency code
        click: {
          actionField: {
            list: 'Related Products', // List name where the impression click occured
          },

          products: [
            {
              id: product.sku, // SKU of the product
              name: product.part_en, // name of the product.
              brand: product.car_brand, // car brand of the part
              category: product.category_en, // Product category(ies) - each "/" is a category level (max 5 levels)
              price: product.pricing?.price_product
                ? Number(product.pricing.price_product).toFixed(2)
                : null, // price of the part without shipping, tax and currency
              position: index, // the position of the item in the list
              dimension2: product.reference, // reference of the product
              dimension3: product.car_model, // car model of the part
              dimension4: product?.car_version, // car version of the part
            },
          ],
        },
      },
    });
  };

  return (
    <div className={cx('block w-full ', bgColor)}>
      <div className="container mx-auto pb-[42px] pt-12">
        <span
          className={cx(
            'container mx-auto',
            'block',
            'font-bold',
            'max-lg:px-8',
            'text-[22px]',
            'lg:text-[32px]',
            'text-center',
            'mb-6'
          )}
        >
          {t('product:similar_parts')}
        </span>
        <Carousel
          opts={{
            align: 'center',
          }}
          viewportBasedSlidesToScroll={{
            desktop: 4,
            mobile: 1,
          }}
        >
          <CarouselContent className="pb-2 lg:-ml-6">
            {relatedProducts.map((product, index) => {
              return <CarouselItem
                key={product.id.toString()}
                className="pl-0 basis-4/5 lg:basis-1/4 lg:pl-6 lg:grid lg:grid-cols-1 lg:gap-6 max-lg:h-fit"
              >
                <BackgroundLink
                  variant="DEFAULT"
                  prefetch={false}
                  shallow
                  scroll
                  {...getLinks('product', {
                    category: product.category_slug,
                    subCategory: product.slug,
                  })}
                  onClick={() => gtmEventOnClick(product, index)}
                >
                  <RelatedProduct
                    product={product}
                    info={{
                      category: product.category_en,
                      subCategory: product.part,
                    }}
                    engineAvailable={referenceVisible.length > 0}
                  />
                </BackgroundLink>
              </CarouselItem>
            })}
          </CarouselContent>
          <CarouselBullets className="!mt-6 max-lg:hidden" />
        </Carousel>
      </div>
    </div>
  );
};

export default RelatedProducts;
